import { timing, variantForLevel } from './params';
const FEEDBACK_TAP_DELAY_MS = 200;
const DIGIT_POOL = 10;
const COLOR_POOL = 8;
function poolSizeFor(mode) {
    return mode === 'digits' ? DIGIT_POOL : COLOR_POOL;
}
function sampleWithReplacement(n, poolSize) {
    const out = [];
    for (let i = 0; i < n; i += 1) {
        out.push(Math.floor(Math.random() * poolSize));
    }
    return out;
}
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    const spanLength = Math.max(2, Math.min(Math.round(value), 12));
    const pool = poolSizeFor(variant.mode);
    const items = sampleWithReplacement(spanLength, pool);
    return {
        trialIdx,
        spanLength,
        items,
        itemDurationMs: variant.itemDurationMs,
    };
}
export function createSequenceAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        currentItemIdx: -1,
        picked: [],
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(picked) {
        if (!resolver || !state.stim)
            return;
        const truth = state.stim.items;
        let hits = 0;
        const n = Math.min(picked.length, truth.length);
        for (let i = 0; i < n; i += 1) {
            if (picked[i] === truth[i])
                hits += 1;
        }
        const correct = picked.length === truth.length &&
            picked.every((p, i) => p === truth[i]);
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            picked: [...picked],
            truth: [...truth],
            hits,
            correct,
            reactionTimeMs: rt,
        });
    }
    function presentItem(stim, idx) {
        if (idx >= stim.items.length) {
            update({ subPhase: 'awaiting_input', currentItemIdx: -1 });
            trialStart = Date.now();
            return;
        }
        update({ subPhase: 'presenting', currentItemIdx: idx });
        timers.push(setTimeout(() => {
            // Gap silencieux entre items.
            update({ currentItemIdx: -1 });
            timers.push(setTimeout(() => {
                presentItem(stim, idx + 1);
            }, timing.gapMs));
        }, stim.itemDurationMs));
    }
    return {
        mount() {
            // Idempotent, rendu declaratif cote React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    currentItemIdx: -1,
                    picked: [],
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'pre_sequence' });
                    timers.push(setTimeout(() => {
                        presentItem(stim, 0);
                    }, timing.preSequenceMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(item) {
            if (state.subPhase !== 'awaiting_input' || !state.stim)
                return;
            const newPicked = [...state.picked, item];
            update({ picked: newPicked });
            if (newPicked.length >= state.stim.spanLength) {
                timers.push(setTimeout(() => {
                    settle(newPicked);
                }, FEEDBACK_TAP_DELAY_MS));
            }
        },
        handleClearLast() {
            if (state.subPhase !== 'awaiting_input')
                return;
            if (state.picked.length === 0)
                return;
            update({ picked: state.picked.slice(0, -1) });
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map